import React from "react";
import { useDisclosure, useToast, Box, Text } from "@chakra-ui/react";
import DeliveryOptionsModal from "./DeliveryOptionsModal";
import {
  DeliveryOptionsLaunchConfig,
  DeliveryOptionsSupplierInput,
  EnrichedSupplierForDelivery,
  NormalizedDeliverySelection,
  DeliveryOption,
} from "./types";
import services from "../../views/Orders/services";
import { META_INFO_KEYS } from "../../utils/constants";
import { getCurrentUTCTimestamp } from "../../utils/dateResolvers";
import { useOrdersStore } from "../../views/Orders/store/useOrdersStore";
import { extractApiErrorMessage, logApiError } from "./utils/apiErrorUtils";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";
import { useGetAllSuppliers } from "../../views/Orders/queryHooks";
import { usePatchEstimateSplit } from "../../views/Orders/hooks/usePatchEstimateSplit";
import { buildDefaultEstimateSplitPatchPayload } from "./utils/estimateSplitPatchPayload";
import { applyEstimateSplitPatchesSequentially } from "./utils/applyEstimateSplitPatchesSequentially";
import { resolveDefaultEstimateSplitTransactionType } from "./utils/resolveDefaultEstimateSplitTransactionType";
import { TransactionType } from "../../views/Orders/enums";
import { isFacilitationTransactionType } from "./utils/estimateSingleInvoiceValidation";
import { normalizeDeliveryOptions, normalizeDeliveryOption, DeliveryOptionInput } from "./utils/deliveryOptionCptSlabUtils";
import { routeRemovedSplitPart } from "./utils/routeRemovedSplitPart";
import { resolveDefaultFulfillmentStatusForSupplier } from "./utils/defaultFulfillmentStatusBySupplier";
import { isEnquiryWhitelistedForLatestEstimateUi } from "./utils/isEnquiryWhitelistedForLatestEstimateUi";
import { EstimateSplitFulfillmentStatus } from "../../views/Orders/enums";
import type { UnassignedPartQty } from "./utils/routeRemovedSplitPart";

export const useDeliveryOptionsLauncher = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { data: configData } = useGetOrderMediaConfig();
  const { data: allSuppliers } = useGetAllSuppliers();
  const setOrderMetaInfoWithOrderId = useOrdersStore(
    (state) => state.setOrderMetaInfoWithOrderId,
  );

  // Get maximum split count allowed for showing share estimate button
  const maxSplitAllowedShareToCustomer: number = React.useMemo(() => {
    return configData?.max_split_allowed_share_to_customer ?? 1;
  }, [configData?.max_split_allowed_share_to_customer]);

  // Parse feature flag - handle both JSON string and boolean/string values
  const ff_share_enquiry_estimate_to_customer: boolean = React.useMemo(() => {
    const flagValue = configData?.ff_share_enquiry_estimate_to_customer;
    return flagValue === "true";
  }, [configData?.ff_share_enquiry_estimate_to_customer]);

  const [isFetchingDeliveryOptions, setIsFetchingDeliveryOptions] =
    React.useState(false);
  const [orderNo, setOrderNo] = React.useState<string>("");
  const [expiresAt, setExpiresAt] = React.useState<string>("");
  const [estimateNo, setEstimateNo] = React.useState<string>("");
  const patchEstimateSplitMutation = usePatchEstimateSplit();
  const handleDeliveryOptionsModalClose = React.useCallback(() => {
    onClose();
  }, [onClose]);
  const [isEstimateShared, setIsEstimateShared] =
    React.useState<boolean>(false);
  const [quickEtasMins, setQuickEtasMins] = React.useState<
    string | undefined
  >();
  const [suppliers, setSuppliers] = React.useState<
    EnrichedSupplierForDelivery[]
  >([]);
  const [onCancelOrder, setOnCancelOrder] = React.useState<
    ((reason: string) => Promise<void> | void) | undefined
  >();
  const [onHoldOrder, setOnHoldOrder] = React.useState<
    (() => Promise<void> | void) | undefined
  >();
  const [onClearBucket, setOnClearBucket] = React.useState<
    (() => void) | undefined
  >();
  const [onSelectionCloseCallback, setOnSelectionCloseCallback] =
    React.useState<(() => void) | undefined>();
  const [onOuterCloseCallback, setOnOuterCloseCallback] = React.useState<
    (() => void) | undefined
  >();
  const [refetchOrders, setRefetchOrders] = React.useState<
    (() => void) | undefined
  >();
  const [orderCancellationReasons, setOrderCancellationReasons] =
    React.useState<string[]>();
  const [isSingleGaraazInvoiceRequired, setIsSingleGaraazInvoiceRequired] =
    React.useState(false);
  const [orderDataForEstimate, setOrderDataForEstimate] =
    React.useState<any>(null);
  const [orderIdForEstimate, setOrderIdForEstimate] = React.useState("");
  const [childOrdersForEstimate, setChildOrdersForEstimate] = React.useState<
    Array<{ isDeleted?: boolean; orderStatus?: string }> | undefined
  >(undefined);
  const [garaazSupplierIdFromConfig, setGaraazSupplierIdFromConfig] =
    React.useState<string | undefined>();
  const onPartsUnassignedRef = React.useRef<
    | ((
        parts: Array<{
          partId?: string;
          partNumber: string;
          quantity: number;
          sourceSupplierId: string;
        }>,
      ) => void)
    | undefined
  >();

  const confirmationNeededSupplierId = React.useMemo(
    () =>
      String(
        (configData as { confirmation_needed_supplier_id?: string } | undefined)
          ?.confirmation_needed_supplier_id ?? "",
      ).trim(),
    [configData],
  );
  const additionalTimeRequiredSupplierId = React.useMemo(
    () =>
      String(
        (
          configData as
            | { additional_time_required_supplier_id?: string }
            | undefined
        )?.additional_time_required_supplier_id ?? "",
      ).trim(),
    [configData],
  );

  const canRemovePartsFromSplits = React.useMemo(
    () =>
      isEnquiryWhitelistedForLatestEstimateUi(
        (configData as { whitelisted_enquiry_nos_for_latest_estimate_ui?: unknown })
          ?.whitelisted_enquiry_nos_for_latest_estimate_ui,
        orderNo,
        orderDataForEstimate,
      ),
    [configData, orderNo, orderDataForEstimate],
  );

  const onConfirmRef = React.useRef<
    | ((
        selections: NormalizedDeliverySelection[],
        splitData: EnrichedSupplierForDelivery[],
        estimateNo: string,
      ) => void)
    | null
  >(null);
  const supplierInputsRef = React.useRef<DeliveryOptionsSupplierInput[]>([]);

  const buildSplitsItemsSyncPayload = React.useCallback(
    (splitList: EnrichedSupplierForDelivery[]) => ({
      splits: splitList
        .filter((s) => Boolean(s.splitId) && s.parts.length > 0)
        .map((s) => ({
          splitId: s.splitId,
          items: s.parts
            .filter((p) => Boolean(p.partId) && p.quantity > 0)
            .map((p) => ({
              partId: String(p.partId),
              qty: p.quantity,
            })),
        }))
        .filter((s) => s.items.length > 0),
    }),
    [],
  );

  const syncSplitsItemsToBackend = React.useCallback(
    async (splitList: EnrichedSupplierForDelivery[], targetEstimateNo: string) => {
      const payload = buildSplitsItemsSyncPayload(splitList);
      await services.syncEstimateSplitsItems(targetEstimateNo, payload);
    },
    [buildSplitsItemsSyncPayload],
  );

  const applyDefaultFulfillmentStatuses = React.useCallback(
    async (
      enriched: EnrichedSupplierForDelivery[],
      targetEstimateNo: string,
    ): Promise<EnrichedSupplierForDelivery[]> => {
      const next = [...enriched];
      for (let i = 0; i < next.length; i++) {
        const supplier = next[i];
        if (
          supplier.supplierId == null ||
          !supplier.splitId ||
          supplier.fulfillmentStatus
        ) {
          continue;
        }
        const defaultStatus = resolveDefaultFulfillmentStatusForSupplier(
          supplier.supplierId,
          confirmationNeededSupplierId,
          additionalTimeRequiredSupplierId,
        );
        if (!defaultStatus) continue;
        try {
          const estimate = await services.patchEstimateSplit(
            targetEstimateNo,
            supplier.splitId,
            { fulfillmentStatus: defaultStatus },
          );
          const updatedSplit = (estimate?.splits ?? []).find(
            (s: { _id?: string }) =>
              String(s._id) === String(supplier.splitId),
          );
          next[i] = {
            ...supplier,
            fulfillmentStatus:
              (updatedSplit?.fulfillmentStatus as EstimateSplitFulfillmentStatus) ||
              defaultStatus,
          };
        } catch {
          next[i] = { ...supplier, fulfillmentStatus: defaultStatus };
        }
      }
      return next;
    },
    [confirmationNeededSupplierId, additionalTimeRequiredSupplierId],
  );

  const fetchAndEnrichSuppliers = React.useCallback(
    async (
      orderNo: string,
      suppliers: DeliveryOptionsSupplierInput[],
    ): Promise<{
      enrichedSuppliers: EnrichedSupplierForDelivery[];
      expiresAt: string;
      estimateNo: string;
    }> => {
      const payload = {
        splits: suppliers.map((supplier) => ({
          supplierId: supplier.supplierId,
          items: supplier.items.map((item) => ({
            partId: item.partId,
            qty: item.quantity,
          })),
          ...(supplier.supplierId && supplier.inventoryItems
            ? { inventoryItems: supplier.inventoryItems }
            : {}),
        })),
      };

      const response = await services.fetchDeliveryOptions(orderNo, payload);
      const splitDataMap = new Map<string | null, any>(
        response.splits.map((split: any) => [split.supplierId ?? null, split]),
      );

      const enrichedSuppliers: EnrichedSupplierForDelivery[] = suppliers.map(
        (s) => {
          const key = s.supplierId ?? null;
          const splitData = splitDataMap.get(key);
          const apiOptions = normalizeDeliveryOptions(
            (splitData?.deliveryOptions || []) as DeliveryOptionInput[],
          );

          const rawSplitId = splitData?._id;
          const splitId =
            rawSplitId != null
              ? typeof rawSplitId === "string"
                ? rawSplitId
                : String(rawSplitId)
              : "";

          return {
            splitId,
            supplierId: s.supplierId,
            supplierName: s.supplierName,
            distanceKm: s.distanceKm,
            supplierCity: s.supplierCity,
            parts: s.items,
            deliveryOptions: apiOptions,
            buyTime: splitData?.buyTime,
            vehicleType: splitData?.vehicleType,
            transactionType: splitData?.transactionType,
            discountPercentage: splitData?.discountPercentage,
            creditNotePercentage: splitData?.creditNotePercentage,
            fulfillmentStatus: splitData?.fulfillmentStatus,
            payableBy: splitData?.payableBy,
            payableTo: splitData?.payableTo,
            supplierOptions: Array.isArray(s.supplierOptions)
              ? s.supplierOptions
              : Array.isArray(splitData?.supplierOptions)
                ? splitData.supplierOptions
                : undefined,
          };
        },
      );

      return {
        enrichedSuppliers,
        expiresAt: response.expiresAt,
        estimateNo: response.estimateNo,
      };
    },
    [],
  );

  const launchDeliveryOptionsModal = React.useCallback(
    async (config: DeliveryOptionsLaunchConfig) => {
      setIsFetchingDeliveryOptions(true);
      try {
        setOrderNo(config.orderNo);
        setQuickEtasMins(config.quickEtasMins);
        setOnCancelOrder(() => config.onCancelOrder);
        setOnHoldOrder(() => config.onHoldOrder);
        setOnClearBucket(() => config.onClearBucket);
        setRefetchOrders(() => config.refetchOrders);
        setOnSelectionCloseCallback(() => config.onSelectionClose);
        setOnOuterCloseCallback(() => config.onClose);
        setOrderCancellationReasons(config.orderCancellationReasons);
        setIsSingleGaraazInvoiceRequired(
          Boolean(config.isSingleGaraazInvoiceRequired),
        );
        setOrderDataForEstimate(config.orderData ?? null);
        setOrderIdForEstimate(config.orderId ?? "");
        setChildOrdersForEstimate(config.childOrders);
        setGaraazSupplierIdFromConfig(config.garaazSupplierIdFromConfig);
        onConfirmRef.current = config.onConfirm;
        onPartsUnassignedRef.current = config.onPartsUnassignedFromEstimate;
        supplierInputsRef.current = config.suppliers;
        const currentUTCTimestamp = getCurrentUTCTimestamp();

        const { enrichedSuppliers, expiresAt, estimateNo } =
          await fetchAndEnrichSuppliers(config.orderNo, config.suppliers);

        const withDefaultFulfillment = await applyDefaultFulfillmentStatuses(
          enrichedSuppliers,
          estimateNo,
        );

        setSuppliers(withDefaultFulfillment);
        setExpiresAt(expiresAt);
        setEstimateNo(estimateNo);
        setIsEstimateShared(false);
        onOpen();

        const applyEstimateToSupplierState = (
          estimate: any,
          targetSplitId: string,
        ) => {
          const updatedSplit = (estimate?.splits ?? []).find(
            (s: any) => String(s._id) === String(targetSplitId),
          );
          if (!updatedSplit) return;
          setSuppliers((prev) =>
            prev.map((s) =>
              s.splitId === targetSplitId
                ? {
                    ...s,
                    transactionType: updatedSplit.transactionType,
                    discountPercentage: updatedSplit.discountPercentage,
                    creditNotePercentage: updatedSplit.creditNotePercentage,
                    fulfillmentStatus: updatedSplit.fulfillmentStatus,
                    payableBy: updatedSplit.payableBy,
                    payableTo: updatedSplit.payableTo,
                    deliveryOptions: updatedSplit.deliveryOptions ?? [],
                  }
                : s,
            ),
          );
        };

        const defaultTransactionTypePatches = withDefaultFulfillment
          .filter(
            (supplier) =>
              supplier.supplierId &&
              supplier.splitId &&
              !supplier.transactionType,
          )
          .map((supplier) => {
            const foundSupplier = allSuppliers?.find((s: any) => String(s._id) === String(supplier.supplierId));
            const parentSupplierId = foundSupplier?.parentSupplierId
              ? String(foundSupplier.parentSupplierId)
              : undefined;

            const defaultType = resolveDefaultEstimateSplitTransactionType({
              supplierId: supplier.supplierId,
              orderData: config.orderData,
              isSingleGaraazInvoiceRequired: Boolean(
                config.isSingleGaraazInvoiceRequired,
              ),
              garaazSupplierIdFromConfig: config.garaazSupplierIdFromConfig,
              mgpDistributorConfig: (configData as any)?.mgp_distributor,
              parentSupplierId,
              allSuppliers,
            });
            if (!defaultType) return null;
            return {
              splitId: supplier.splitId,
              payload: buildDefaultEstimateSplitPatchPayload(
                defaultType,
                config.orderData,
              ),
            };
          })
          .filter(
            (patch): patch is { splitId: string; payload: ReturnType<typeof buildDefaultEstimateSplitPatchPayload> } =>
              patch != null,
          );

        for (const patch of defaultTransactionTypePatches) {
          const estimate = await applyEstimateSplitPatchesSequentially(
            estimateNo,
            [patch],
          );
          if (estimate) {
            applyEstimateToSupplierState(estimate, patch.splitId);
          }
        }

        if (Boolean(config.isSingleGaraazInvoiceRequired)) {
          const tradingMigrationPatches = withDefaultFulfillment
            .filter(
              (supplier) =>
                supplier.supplierId &&
                supplier.splitId &&
                isFacilitationTransactionType(supplier.transactionType),
            )
            .map((supplier) => ({
              splitId: supplier.splitId,
              payload: buildDefaultEstimateSplitPatchPayload(
                TransactionType.TRADING,
                config.orderData,
              ),
            }));

          for (const patch of tradingMigrationPatches) {
            const estimate = await applyEstimateSplitPatchesSequentially(
              estimateNo,
              [patch],
            );
            if (estimate) {
              applyEstimateToSupplierState(estimate, patch.splitId);
            }
          }
        }

        // Update meta info with estimateSharedAt timestamp in the background (non-blocking)
        if (config.orderId) {
          services
            .updateOrderMetaInfo(config.orderId, {
              [META_INFO_KEYS.ESTIMATE_SHARED_AT]: currentUTCTimestamp,
            })
            .then(() => {
              setOrderMetaInfoWithOrderId(config.orderId, {
                [META_INFO_KEYS.ESTIMATE_SHARED_AT]: currentUTCTimestamp,
              });
            })
            .catch((error) => {
              // Non-blocking: Log error but don't prevent modal from opening
              console.error("Failed to update estimateSharedAt:", error);
            });
        }
      } catch (error: any) {
        // Log full error details for debugging
        logApiError(error, "fetchDeliveryOptions");

        // Extract and display user-friendly error message
        const errorMessage = extractApiErrorMessage(
          error,
          "Failed to fetch delivery options. Please try again."
        );

        // Split message by newlines and create a formatted component for multi-line display
        const messageLines = errorMessage.split("\n");
        const isMultiLine = messageLines.length > 1;

        toast({
          title: "Failed to fetch delivery options",
          description: isMultiLine ? undefined : errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
          render: isMultiLine
            ? () => (
                <Box
                  p="4"
                  borderRadius="md"
                  bg="red.500"
                  color="white"
                  boxShadow="lg"
                >
                  <Text fontWeight="bold" fontSize="md" mb="2">
                    Failed to fetch delivery options
                  </Text>
                  <Box>
                    {messageLines.map((line, index) => (
                      <Text key={index} fontSize="sm" mb="1">
                        {index + 1}. {line}
                      </Text>
                    ))}
                  </Box>
                </Box>
              )
            : undefined,
        });
      } finally {
        setIsFetchingDeliveryOptions(false);
      }
    },
    [
      onOpen,
      fetchAndEnrichSuppliers,
      toast,
      setOrderMetaInfoWithOrderId,
      patchEstimateSplitMutation,
      allSuppliers,
      configData,
      applyDefaultFulfillmentStatuses,
    ]
  );

  const executeShareEstimate = React.useCallback(async () => {
    if (!estimateNo) {
      toast({
        title: "Share Estimate",
        description: "Estimate number is missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      await syncSplitsItemsToBackend(suppliers, estimateNo);
      await services.shareEstimate(estimateNo);
      setIsEstimateShared(true);
      toast({
        title: "Share Estimate",
        description: "Estimate shared successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      refetchOrders?.();
      handleDeliveryOptionsModalClose();
      onSelectionCloseCallback?.();
      onOuterCloseCallback?.();
    } catch (error: any) {
      logApiError(error, "shareEstimate");
      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to share estimate. Please try again.",
      );
      toast({
        title: "Share Estimate",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }, [
    estimateNo,
    suppliers,
    syncSplitsItemsToBackend,
    toast,
    refetchOrders,
    handleDeliveryOptionsModalClose,
    onSelectionCloseCallback,
    onOuterCloseCallback,
  ]);

  const handleShareEstimate = React.useCallback(async () => {
    await executeShareEstimate();
  }, [executeShareEstimate]);

  const handleConfirm = React.useCallback(
    async (
      selections: NormalizedDeliverySelection[],
      splitData: EnrichedSupplierForDelivery[],
    ) => {
      if (estimateNo) {
        try {
          await syncSplitsItemsToBackend(splitData, estimateNo);
        } catch (error: any) {
          logApiError(error, "syncEstimateSplitsItems");
          const errorMessage = extractApiErrorMessage(
            error,
            "Failed to sync estimate parts. Please try again.",
          );
          toast({
            title: "Proceed Estimate",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          return;
        }
      }
      handleDeliveryOptionsModalClose();
      onConfirmRef.current?.(selections, splitData, estimateNo);
    },
    [handleDeliveryOptionsModalClose, estimateNo, syncSplitsItemsToBackend, toast],
  );

  const handleRemovePartFromSplit = React.useCallback(
    (splitId: string, partIndex: number) => {
      let unassigned: UnassignedPartQty | undefined;
      setSuppliers((prev) => {
        const result = routeRemovedSplitPart({
          splits: prev,
          sourceSplitId: splitId,
          partIndex,
          confirmationNeededSupplierId,
          additionalTimeRequiredSupplierId,
        });
        unassigned = result.unassigned;
        return result.splits;
      });
      if (unassigned) {
        onPartsUnassignedRef.current?.([
          {
            partId: unassigned.partId,
            partNumber: unassigned.partNumber,
            quantity: unassigned.quantity,
            sourceSupplierId: unassigned.sourceSupplierId,
          },
        ]);
      }
    },
    [confirmationNeededSupplierId, additionalTimeRequiredSupplierId],
  );

  const handleOptionUpdate = React.useCallback(
    (splitId: string, updatedOption: DeliveryOption) => {
      setSuppliers((prev) =>
        prev.map((s) =>
          s.splitId === splitId
            ? {
                ...s,
                deliveryOptions: s.deliveryOptions.map((o) =>
                  o._id === updatedOption._id
                    ? normalizeDeliveryOption(updatedOption)
                    : o,
                ),
              }
            : s,
        ),
      );
    },
    [],
  );

  const handleOptionAdd = React.useCallback(
    (splitId: string, newOption: DeliveryOption) => {
      setSuppliers((prev) =>
        prev.map((s) =>
          s.splitId === splitId
            ? {
                ...s,
                deliveryOptions: [
                  ...s.deliveryOptions,
                  normalizeDeliveryOption(newOption),
                ],
              }
            : s,
        ),
      );
    },
    [],
  );

  const handleSupplierSplitConfigUpdate = React.useCallback(
    (
      splitId: string,
      update: {
        transactionType?: string;
        discountPercentage?: number;
        creditNotePercentage?: number;
        fulfillmentStatus?: EnrichedSupplierForDelivery["fulfillmentStatus"];
        payableBy?: EnrichedSupplierForDelivery["payableBy"];
        payableTo?: EnrichedSupplierForDelivery["payableTo"];
        deliveryOptions: DeliveryOption[];
      },
    ) => {
      setSuppliers((prev) =>
        prev.map((s) =>
          s.splitId === splitId
            ? {
                ...s,
                transactionType:
                  "transactionType" in update
                    ? update.transactionType
                    : s.transactionType,
                discountPercentage:
                  "discountPercentage" in update
                    ? update.discountPercentage
                    : s.discountPercentage,
                creditNotePercentage:
                  "creditNotePercentage" in update
                    ? update.creditNotePercentage
                    : s.creditNotePercentage,
                fulfillmentStatus:
                  "fulfillmentStatus" in update
                    ? update.fulfillmentStatus
                    : s.fulfillmentStatus,
                payableBy:
                  "payableBy" in update ? update.payableBy : s.payableBy,
                payableTo: "payableTo" in update ? update.payableTo : s.payableTo,
                deliveryOptions: normalizeDeliveryOptions(
                  update.deliveryOptions as DeliveryOptionInput[],
                ),
              }
            : s,
        ),
      );
    },
    [],
  );

  const migrateFacilitationSplitsToTrading = React.useCallback(
    async (supplierList?: EnrichedSupplierForDelivery[]) => {
    if (!estimateNo) return;

    const splitsToMigrate = (supplierList ?? suppliers).filter(
      (supplier) =>
        supplier.supplierId != null &&
        supplier.splitId &&
        isFacilitationTransactionType(supplier.transactionType),
    );

    if (splitsToMigrate.length === 0) return;

    const migrationPatches = splitsToMigrate.map((supplier) => ({
      splitId: supplier.splitId,
      payload: buildDefaultEstimateSplitPatchPayload(
        TransactionType.TRADING,
        orderDataForEstimate,
      ),
    }));

    for (const patch of migrationPatches) {
      const estimate = await applyEstimateSplitPatchesSequentially(
        estimateNo,
        [patch],
      );
      const updatedSplit = (estimate as any)?.splits?.find(
        (s: any) => String(s._id) === String(patch.splitId),
      );
      if (updatedSplit) {
        handleSupplierSplitConfigUpdate(patch.splitId, {
          transactionType: updatedSplit.transactionType,
          discountPercentage: updatedSplit.discountPercentage,
          creditNotePercentage: updatedSplit.creditNotePercentage,
          fulfillmentStatus: updatedSplit.fulfillmentStatus,
          payableBy: updatedSplit.payableBy,
          payableTo: updatedSplit.payableTo,
          deliveryOptions: updatedSplit.deliveryOptions ?? [],
        });
      }
    }

    toast({
      title: "Transaction type updated",
      description:
        "All splits transaction type has been changed to Trading.",
      status: "info",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  },
  [
    estimateNo,
    suppliers,
    orderDataForEstimate,
    patchEstimateSplitMutation,
    handleSupplierSplitConfigUpdate,
    toast,
  ],
  );

  const handleOptionDelete = React.useCallback(
    (splitId: string, optionId: string) => {
      setSuppliers((prev) =>
        prev.map((s) =>
          s.splitId === splitId
            ? {
                ...s,
                deliveryOptions: s.deliveryOptions.filter(
                  (o) => o._id !== optionId,
                ),
              }
            : s,
        ),
      );
    },
    [],
  );

  const handleHoldOrder = React.useCallback(async () => {
    if (onHoldOrder) {
      await onHoldOrder();
    }

    if (estimateNo && suppliers.length > 0) {
      const splitsToHold = suppliers.filter((s) => s.splitId);
      const results = await Promise.allSettled(
        splitsToHold.map((s) =>
          services.holdEstimateSplit(estimateNo, s.splitId),
        ),
      );

      const failures = results
        .map((r, i) => (r.status === "rejected" ? splitsToHold[i] : null))
        .filter(Boolean);

      if (failures.length > 0) {
        toast({
          title: "Hold Estimate",
          description: `Failed to hold ${failures.length}/${splitsToHold.length} splits.`,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } else {
        toast({
          title: "Hold Estimate",
          description: "All splits held successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    }

    refetchOrders?.();
    handleDeliveryOptionsModalClose();
  }, [
    onHoldOrder,
    handleDeliveryOptionsModalClose,
    estimateNo,
    suppliers,
    toast,
    refetchOrders,
  ]);

  const handleClearBucketAndClose = React.useCallback(() => {
    onClearBucket?.();
    handleDeliveryOptionsModalClose();
  }, [onClearBucket, handleDeliveryOptionsModalClose]);

  const handleCancelEstimate = React.useCallback(
    async (reason: string) => {
      if (onCancelOrder) {
        try {
          await onCancelOrder(reason);
          toast({
            title: "Cancel Enquiry",
            description: "Enquiry cancelled successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            "Failed to cancel enquiry.",
          );
          toast({
            title: "Cancel Enquiry",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      }

      if (estimateNo && suppliers.length > 0) {
        const splitsToCancel = suppliers.filter((s) => s.splitId);
        const results = await Promise.allSettled(
          splitsToCancel.map((s) =>
            services.cancelEstimateSplit(estimateNo, s.splitId),
          ),
        );

        const failures = results
          .map((r, i) => (r.status === "rejected" ? splitsToCancel[i] : null))
          .filter(Boolean);

        if (failures.length > 0) {
          toast({
            title: "Cancel Enquiry",
            description: `Failed to cancel ${failures.length}/${splitsToCancel.length} splits.`,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        } else {
          toast({
            title: "Cancel Estimate",
            description: "All splits cancelled successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }

      refetchOrders?.();
      onSelectionCloseCallback?.();
      onOuterCloseCallback?.();
    },
    [
      onCancelOrder,
      estimateNo,
      suppliers,
      toast,
      refetchOrders,
      onSelectionCloseCallback,
      onOuterCloseCallback,
    ],
  );

  const deliveryOptionsModal = (
    <DeliveryOptionsModal
      isOpen={isOpen}
      onClose={handleDeliveryOptionsModalClose}
      orderNo={orderNo}
      suppliers={suppliers}
      expiresAt={expiresAt}
      onShareEstimate={
        ff_share_enquiry_estimate_to_customer ? handleShareEstimate : undefined
      }
      isEstimateShared={isEstimateShared}
      maxSplitAllowedShareToCustomer={maxSplitAllowedShareToCustomer}
      onConfirm={handleConfirm}
      onOptionUpdate={handleOptionUpdate}
      onOptionAdd={handleOptionAdd}
      onOptionDelete={handleOptionDelete}
      onHoldOrder={handleHoldOrder}
      onClearBucket={handleClearBucketAndClose}
      quickEtasMins={quickEtasMins}
      instantDeliveryMinimumDeliveryCost={
        configData?.instant_delivery_min_delivery_cost_rs
      }
      onCancelOrder={handleCancelEstimate}
      orderCancellationReasons={orderCancellationReasons}
      estimateNo={estimateNo}
      isSingleGaraazInvoiceRequired={isSingleGaraazInvoiceRequired}
      orderId={orderIdForEstimate}
      orderData={orderDataForEstimate}
      refetchOrders={refetchOrders}
      garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
      childOrders={childOrdersForEstimate}
      onSupplierSplitConfigUpdate={handleSupplierSplitConfigUpdate}
      onRemovePartFromSplit={handleRemovePartFromSplit}
      canRemovePartsFromSplits={canRemovePartsFromSplits}
      onSingleInvoiceToggleChange={async (enabled) => {
        setIsSingleGaraazInvoiceRequired(enabled);
        if (enabled) {
          await migrateFacilitationSplitsToTrading();
        }
      }}
    />
  );

  return {
    isDeliveryOptionsOpen: isOpen,
    isFetchingDeliveryOptions,
    launchDeliveryOptionsModal,
    deliveryOptionsModal,
    estimateNo,
    executeShareEstimate,
  };
};

export default useDeliveryOptionsLauncher;
